import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../utils/db';
import { pendingProposals } from '../../utils/dbSelectors';
import { interpret } from '../../utils/intents';
import { playSound } from '../../utils/sound';
import { cancelSpeech, speakDutch } from '../../utils/tts';
import { useDb } from '../../hooks/useDb';
import { useAgentSim } from '../../hooks/useAgentSim';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import type { AgentId, AvatarState, HouseMode, Processing, SoundId } from '../../types/anna';
import type { Activity, CardSpawn, Daypart, StageCard, StageEffect, StageView } from '../../types/stage';

const WORD_MS = 330;
const MAX_CARDS = 3;
const FOCUS_LINE = 'Focus · ik meld alleen wat dringend is';

/** The stage director: scenario moments, Anna's speech, summoned cards and approvals. Data lives in the fake db. */
export function useAnnaStage() {
  const { soundOn } = useSpecSettings();
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const moments = useDb((t) => t.moments);
  const meta = useDb((t) => t.meta);
  const pending = useDb(pendingProposals);
  const roomIds = useDb((t) => t.rooms.map((r) => r.id));

  const first = moments[0];
  const [momentIndex, setMomentIndex] = useState(0);
  const [daypart, setDaypart] = useState<Daypart>(first.daypart);
  const [base, setBase] = useState<AvatarState>(first.base);
  const [view, setView] = useState<StageView>(first.view);
  const [quietLine, setQuietLine] = useState<string | undefined>(first.quietLine);
  const [activity, setActivity] = useState<Activity>('idle');
  const [thinkingWhere, setThinkingWhere] = useState<Processing>('lokaal');
  const [caption, setCaption] = useState<{ id: number; text: string } | null>(null);
  const [revealed, setRevealed] = useState(0);
  const [cards, setCards] = useState<StageCard[]>([]);
  const [outcomes, setOutcomes] = useState<Record<string, 'ok' | 'nee'>>({});
  const [pulseKey, setPulseKey] = useState(0);
  const [flash, setFlash] = useState<{ agent: AgentId; id: number } | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string; tone: SoundId } | null>(null);
  const [stopped, setStopped] = useState(false);
  const [transient, setTransient] = useState<AvatarState | null>(null);
  /** Bumps when the demo data resets, so the agent simulation restarts with it */
  const [epoch, setEpoch] = useState(0);

  const mode = meta.mode;
  const modeRef = useRef<HouseMode>(mode);
  modeRef.current = mode;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const viewRef = useRef(view);
  viewRef.current = view;

  const timers = useRef<number[]>([]);
  const speechId = useRef(0);
  const uid = useRef(0);

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);
  useEffect(
    () => () => {
      clearTimers();
      cancelSpeech();
    },
    [clearTimers],
  );

  const isQuiet = () => modeRef.current === 'Focus' || modeRef.current === 'Slapen';
  /** Sounds are muted in Niet storen / Slapen — except real safety alarms. */
  const sound = useCallback((id: SoundId, safety = false) => {
    if (!soundRef.current) return;
    if (!safety && (modeRef.current === 'Focus' || modeRef.current === 'Slapen')) return;
    playSound(id);
  }, []);

  const setMode = (m: HouseMode) => {
    modeRef.current = m;
    db.setMeta({ mode: m });
  };

  const showToast = useCallback((text: string, tone: SoundId) => setToast({ id: Date.now(), text, tone }), []);

  const addCard = useCallback((spawn: CardSpawn) => {
    setCards((prev) => {
      const without = prev.filter((c) => c.kind !== spawn.kind);
      const left = without.filter((c) => c.side === 'links').length;
      const side = spawn.side ?? (left <= without.length - left ? 'links' : 'rechts');
      return [...without, { uid: `c${uid.current++}`, kind: spawn.kind, side, payload: spawn.payload }].slice(-MAX_CARDS);
    });
  }, []);

  const removeCard = useCallback((cardUid: string) => setCards((prev) => prev.filter((c) => c.uid !== cardUid)), []);

  const speakLine = useCallback(
    (text: string, spawns: CardSpawn[] = [], volume = 1) => {
      const id = ++speechId.current;
      const words = text.split(' ');
      setCaption({ id, text });
      setRevealed(0);
      setActivity('speaking');
      words.forEach((_, i) => later(() => setRevealed(i + 1), i * WORD_MS));
      spawns.forEach((s) => later(() => addCard(s), (s.atWord ?? 0) * WORD_MS));

      let timerDone = false;
      let ttsDone = !soundRef.current;
      const finish = () => {
        if (!timerDone || !ttsDone || speechId.current !== id) return;
        setActivity((a) => (a === 'speaking' ? 'idle' : a));
        // Captions fade 6 s after the last word.
        later(() => setCaption((c) => (c && c.id === id ? null : c)), 6000);
      };
      const minEnd = words.length * WORD_MS + 300;
      later(() => {
        timerDone = true;
        finish();
      }, minEnd);
      later(() => {
        ttsDone = true;
        finish();
      }, minEnd + 8000);
      if (soundRef.current) {
        const ok = speakDutch(text, {
          volume,
          onEnd: () => {
            ttsDone = true;
            finish();
          },
        });
        if (!ok) ttsDone = true;
      }
    },
    [addCard, later],
  );

  /** Owner interrupts: Anna stops mid-sentence immediately. */
  const bargeIn = useCallback(() => {
    speechId.current++;
    clearTimers();
    cancelSpeech();
    setCaption(null);
    setActivity('idle');
  }, [clearTimers]);

  const goToMoment = useCallback(
    (index: number) => {
      const m = moments[index];
      speechId.current++;
      clearTimers();
      cancelSpeech();
      setMomentIndex(index);
      modeRef.current = m.mode;
      if (m.id === 'rust') {
        db.reset();
        setEpoch((e) => e + 1);
      }
      db.setMeta({ time: m.time, mode: m.mode });
      setDaypart(m.daypart);
      setBase(m.base);
      setView(m.view);
      setQuietLine(m.quietLine);
      setTransient(null);
      setActivity('idle');
      setCaption(null);
      if (!m.keepCards) setCards([]);

      // Keep the fake database in step with the scenario.
      if (m.id === 'ochtend') db.setDevice('koffie', 'aan');
      if (m.id === 'deur') {
        db.log({ agent: 'huis', summary: 'Bezorger aan de deur, stil gemeld', level: 'zelf', where: 'lokaal', outcome: 'gemeld', traceId: 'deur' });
      }
      if (m.view === 'approvals') {
        db.resetProposals();
        setOutcomes({});
      }
      if (m.view === 'alarm') {
        db.setDevice('water-keuken', 'nat');
        db.log({ agent: 'huis', summary: 'Water in de keuken gemeld', level: 'zelf', where: 'lokaal', outcome: 'gemeld', traceId: 'waterlek' });
      }

      const quiet = m.mode === 'Focus' || m.mode === 'Slapen';
      const start = m.keepCards ? 150 : 520;
      if (m.pulse) later(() => setPulseKey((k) => k + 1), start);
      if (m.chime) {
        later(() => {
          setPulseKey((k) => k + 1);
          sound('meldt');
        }, start);
      }
      if (m.view === 'alarm') later(() => sound('meldt', true), start);
      const speakAt = start + (m.chime ? 900 : 0);
      if (m.speech) {
        later(() => speakLine(m.speech as string, m.cards ?? [], quiet && m.view !== 'alarm' ? 0.5 : 1), speakAt);
      } else {
        (m.cards ?? []).forEach((c, i) => later(() => addCard(c), start + 120 * i));
      }
    },
    [addCard, clearTimers, later, moments, sound, speakLine],
  );

  const next = useCallback(() => goToMoment((momentIndex + 1) % moments.length), [goToMoment, momentIndex, moments.length]);
  const prev = useCallback(
    () => goToMoment((momentIndex - 1 + moments.length) % moments.length),
    [goToMoment, momentIndex, moments.length],
  );

  const openApprovals = useCallback(() => {
    bargeIn();
    setCards([]);
    setOutcomes({});
    setView('approvals');
  }, [bargeIn]);

  const leaveApprovals = useCallback(() => setView('stage'), []);

  const settle = useCallback(
    (id: string, outcome: 'ok' | 'nee') => {
      const p = pendingRef.current.find((x) => x.id === id);
      if (!p) return;
      const remaining = pendingRef.current.length - 1;
      // Outcome first, so the card knows which way to exit; the db write follows a tick later.
      setOutcomes((o) => ({ ...o, [id]: outcome }));
      if (outcome === 'ok') {
        window.setTimeout(() => db.approveProposal(id), 0);
        sound('klaar');
        setFlash({ agent: p.agent, id: Date.now() });
        // The card flies into Anna; her thread for that agent lights up.
        setTransient('werkt');
        later(() => setTransient(null), 2400);
      } else {
        window.setTimeout(() => db.rejectProposal(id), 0);
        showToast('Afgewezen. Anna leert hiervan.', 'geweigerd');
      }
      if (remaining <= 0) {
        later(() => {
          setView('stage');
          speakLine('Alles afgehandeld.');
        }, 900);
      }
    },
    [later, showToast, sound, speakLine],
  );

  const applyEffect = (effect?: StageEffect) => {
    if (!effect) return;
    if (effect.type === 'light') db.setLight(effect.room, effect.value);
    if (effect.type === 'target') db.setTarget(effect.room, effect.value);
    if (effect.type === 'coffee') {
      db.setDevice('koffie', 'aan');
      db.log({ agent: 'huis', summary: 'Koffiezetapparaat aan', level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd', traceId: 'koffie' });
    }
    if (effect.type === 'refuse') {
      db.log({
        agent: 'huis',
        summary: `Verzoek geweigerd: ${effect.subject}`,
        level: 'nooit',
        where: 'lokaal',
        outcome: 'geweigerd',
        traceId: `weigering-${effect.subject}`,
      });
    }
  };

  const respond = useCallback(
    (text: string) => {
      const res = interpret(text, { mode: modeRef.current, waiting: pendingRef.current.length, roomIds });
      if (res.action === 'ochtend') {
        goToMoment(1);
        return;
      }
      setActivity('thinking');
      setThinkingWhere(res.where);
      later(() => {
        if (res.tone) sound(res.tone);
        if (res.concerned) {
          setTransient('bezorgd');
          later(() => setTransient(null), 4500);
        }
        applyEffect(res.effect);
        switch (res.action) {
          case 'approvals':
            setCards([]);
            setOutcomes({});
            setView('approvals');
            break;
          case 'focus':
            setMode('Focus');
            setDaypart('focus');
            setBase('nietStoren');
            setView('stage');
            setQuietLine(FOCUS_LINE);
            setCards([]);
            break;
          case 'slapen':
            setMode('Slapen');
            setDaypart('nacht');
            setBase('slaapt');
            setView('sleep');
            setQuietLine(undefined);
            setCards([]);
            break;
          case 'vrij':
            setMode('Vrij');
            setDaypart((d) => (d === 'focus' || d === 'nacht' || d === 'alarm' ? 'middag' : d));
            setBase('rust');
            setView('stage');
            setQuietLine(undefined);
            break;
          case 'werkt':
            setTransient('werkt');
            later(() => setTransient(null), 9000);
            break;
          default:
            if (viewRef.current === 'sleep' && res.cards?.length) setView('stage');
        }
        if (res.toast) showToast(res.toast, res.tone ?? 'klaar');
        speakLine(res.say, res.cards ?? [], isQuiet() ? 0.5 : 1);
      }, 700);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goToMoment, later, roomIds, showToast, sound, speakLine],
  );

  const setListening = useCallback((on: boolean) => {
    setActivity((a) => (on ? 'listening' : a === 'listening' ? 'idle' : a));
  }, []);

  const emergencyStop = useCallback(() => {
    bargeIn();
    setCards((prevCards) => prevCards.filter((c) => c.kind !== 'verwarming'));
    db.log({ agent: 'systeem', summary: 'Noodstop: alle agents gestopt', level: 'zelf', where: 'lokaal', outcome: 'gestopt' });
    setStopped(true);
  }, [bargeIn]);

  const resume = useCallback(() => {
    db.log({ agent: 'systeem', summary: 'Anna hervat', level: 'zelf', where: 'lokaal', outcome: 'uitgevoerd' });
    setStopped(false);
  }, []);

  const ackAlarm = useCallback(() => {
    bargeIn();
    db.setDevice('water-keuken', 'gezien');
    setView('sleep');
    setBase('slaapt');
    setDaypart('nacht');
  }, [bargeIn]);

  const notifyApproved = useCallback(
    (agent: AgentId) => {
      sound('klaar');
      setFlash({ agent, id: Date.now() });
    },
    [sound],
  );

  // The wall opens on the first moment, whatever another screen did to the shared data.
  useEffect(() => {
    db.setMeta({ time: first.time, mode: first.mode });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Studio finishes in the background: its satellite flashes, a short rising tone. */
  const finishRender = useCallback(() => {
    const r = db.getState().renders[0];
    if (!r || r.status === 'klaar') return;
    db.finishRender(r.id);
    setFlash({ agent: 'studio', id: Date.now() });
    sound('klaar');
  }, [sound]);

  useAgentSim(epoch, stopped, finishRender);

  const avatarState: AvatarState = stopped
    ? 'gestopt'
    : activity === 'listening'
      ? 'luistert'
      : activity === 'thinking'
        ? 'denkt'
        : activity === 'speaking'
          ? 'spreekt'
          : transient ?? base;

  return {
    moment: moments[momentIndex],
    momentIndex,
    momentCount: moments.length,
    mode,
    daypart,
    view,
    quietLine,
    activity,
    thinkingWhere,
    caption,
    revealed,
    cards,
    pending,
    outcomes,
    pulseKey,
    flash,
    toast,
    stopped,
    avatarState,
    quiet: mode === 'Focus' || mode === 'Slapen',
    next,
    prev,
    goToMoment,
    respond,
    bargeIn,
    setListening,
    openApprovals,
    leaveApprovals,
    approve: (id: string) => settle(id, 'ok'),
    reject: (id: string) => settle(id, 'nee'),
    removeCard,
    showToast,
    dismissToast: () => setToast(null),
    emergencyStop,
    resume,
    ackAlarm,
    notifyApproved,
    sound,
  };
}

export type AnnaStage = ReturnType<typeof useAnnaStage>;
